import { formatDate, toCalendarDate } from '@/utils'
import { useStatements } from '../../hooks/project'
import type {
  SecuritiesAccount,
  SecuritiesActivity,
  SecuritiesHolding,
  SecuritiesRealizedGain,
  SecuritiesSectionCheck,
  SecuritiesUnvestedAward,
  SecuritiesYearToDate,
  Statement,
  StatementSegment,
} from '../../stores/types'
import { printedMonthsOf } from './model/months'
import type {
  Holding,
  PortfolioAccount,
  PortfolioActivity,
  PortfolioPeriod,
  PrintedTotal,
  RealizedGain,
  RowSource,
  SectionValidation,
  UnvestedAward,
  YearToDateFigure,
} from './types'

// The engine sends account_type lowercase; the badge wants the capitalized label. Anything
// unknown lands on 'Investment' so a securities account still shows up instead of being dropped.
const toAccountType = (
  accountType: string | null,
): PortfolioAccount['accountType'] =>
  accountType?.toLowerCase() === 'retirement' ? 'Retirement' : 'Investment'

const periodLabel = (segment: StatementSegment): string => {
  if (segment.endDate) return formatDate(segment.endDate, 'MMM yyyy')
  if (segment.startDate) return formatDate(segment.startDate, 'MMM yyyy')
  return segment.name ?? 'Statement period'
}

// The period key the dropdown selects on and the chart reads as `${period}-01`. A month key
// (YYYY-MM) off the segment's end date keeps the x-axis real. A dateless segment falls back to
// its id, which still keys the dropdown uniquely (it just won't plot a dated point).
const periodKey = (segment: StatementSegment): string => {
  const periodDate = segment.endDate ?? segment.startDate
  if (periodDate) return formatDate(periodDate, 'yyyy-MM')
  return `segment-${segment.id}`
}

// Row ids must be globally unique across the flattened tables. One segment can hold many securities
// accounts, each re-indexing its rows from 0, so the segment id alone collides between accounts.
// The account last-4 disambiguates them.
const rowPrefix = (segment: StatementSegment, last4: string): string =>
  `segment-${segment.id}-${last4}`

// The source document a securities row came from. The page is per-row; the file name and URL come
// from the segment's parent statement, so every table can link back to the exact page.
const sourceOf = (
  statement: Statement,
  sourcePage: number | null,
): RowSource => ({
  statementFileName: statement.fileName,
  statementFileUrl: statement.fileUrl,
  sourcePage,
})

const toHolding = (
  holding: SecuritiesHolding,
  segment: StatementSegment,
  statement: Statement,
  index: number,
  last4: string,
): Holding => ({
  id: `${rowPrefix(segment, last4)}-holding-${index}`,
  symbol: holding.symbol ?? '',
  description: holding.description ?? '',
  assetClass: holding.assetClass ?? '',
  quantity: holding.quantity,
  price: holding.marketPrice,
  marketValue: holding.marketValue,
  costBasis: holding.costBasis,
  unrealizedGainLoss: holding.unrealizedGainLoss,
  subAccount: holding.subAccount ?? '',
  ...sourceOf(statement, holding.sourcePage),
})

const toActivity = (
  activity: SecuritiesActivity,
  segment: StatementSegment,
  statement: Statement,
  index: number,
  last4: string,
): PortfolioActivity => ({
  id: `${rowPrefix(segment, last4)}-activity-${index}`,
  // Keep the canonical YYYY-MM-DD so the table sorts and the date filter compares right.
  date: activity.date ? toCalendarDate(activity.date) : '',
  type: activity.activityType ?? '',
  description: activity.description ?? '',
  symbol: activity.symbol ?? undefined,
  quantity: activity.quantity ?? undefined,
  amount: activity.cashAmount,
  settlementDate: activity.settlementDate
    ? toCalendarDate(activity.settlementDate)
    : '',
  price: activity.price ?? null,
  unsettled: activity.activityType === 'unsettled_trade',
  commissionFees: activity.commissionFees ?? null,
  ...sourceOf(statement, activity.sourcePage),
})

const toRealizedGain = (
  gain: SecuritiesRealizedGain,
  segment: StatementSegment,
  statement: Statement,
  index: number,
  last4: string,
): RealizedGain => ({
  id: `${rowPrefix(segment, last4)}-gain-${index}`,
  description: gain.description ?? '',
  symbol: gain.symbol ?? undefined,
  quantity: gain.quantity ?? undefined,
  liquidationDate: gain.liquidationDate
    ? toCalendarDate(gain.liquidationDate)
    : '',
  saleAmount: gain.saleAmount,
  costBasis: gain.costBasis,
  gainLoss: gain.gainLoss,
  term: gain.term ?? undefined,
  ...sourceOf(statement, gain.sourcePage),
})

const toUnvestedAward = (
  award: SecuritiesUnvestedAward,
  segment: StatementSegment,
  statement: Statement,
  index: number,
  last4: string,
): UnvestedAward => ({
  id: `${rowPrefix(segment, last4)}-award-${index}`,
  grantDate: award.grantDate ? toCalendarDate(award.grantDate) : '',
  awardNumber: award.awardNumber ?? '',
  awardType: award.awardType ?? '',
  symbol: award.symbol ?? '',
  subAccount: award.subAccount ?? '',
  quantity: award.quantity,
  grantPrice: award.grantPrice,
  marketPrice: award.marketPrice,
  marketValue: award.marketValue,
  ...sourceOf(statement, award.sourcePage),
})

const toYearToDate = (
  figure: SecuritiesYearToDate,
  segment: StatementSegment,
  statement: Statement,
  index: number,
  last4: string,
): YearToDateFigure => ({
  id: `${rowPrefix(segment, last4)}-ytd-${index}`,
  label: figure.label,
  amount: figure.amount,
  ...sourceOf(statement, figure.sourcePage),
})

// Only the engine's own failed verdict counts. A section the statement never printed a total for
// carries matches: false by design and is informational, not a failure. Older stored payloads
// have no status, so a printed total that did not match is the failure signal there.
const isFailed = (check: SecuritiesSectionCheck): boolean =>
  check.status == null
    ? check.printedTotal != null && !check.matches
    : check.status === 'failed'

// The cash side reconciles each account's sweep balance under its last-4. A cycle with a single
// brokerage account can fall back to the cycle's own ending balance.
const cashBalanceFor = (
  segment: StatementSegment,
  last4: string,
  accountsInSegment: number,
): number | null => {
  const chains = segment.reconciliationDetail?.accounts ?? []
  const chain = chains.find((entry) => entry.last4 === last4)
  if (chain) return chain.end
  if (accountsInSegment === 1 && chains.length <= 1) return segment.endBalance
  return null
}

const cashGapFor = (
  segment: StatementSegment,
  last4: string,
): number | null => {
  const chain = segment.reconciliationDetail?.accounts?.find(
    (entry) => entry.last4 === last4,
  )
  if (!chain || chain.reconciled || !chain.gap) return null
  return chain.gap
}

const realizedPrinted = (account: SecuritiesAccount): boolean =>
  account.checks.some(
    (check) => check.section === 'realized_gains' && check.printedTotal != null,
  )

const failingSections = (account: SecuritiesAccount): SectionValidation[] =>
  account.checks.filter(isFailed).map((check) => ({
    section: check.section,
    matches: check.matches,
    delta: check.delta,
    anchorMissing: check.anchorMissing,
    printedTotal: check.printedTotal,
    extractedTotal: check.extractedTotal,
  }))

const printedTotals = (account: SecuritiesAccount): PrintedTotal[] =>
  account.checks.flatMap((check) =>
    check.printedTotal == null
      ? []
      : [{ section: check.section, amount: check.printedTotal }],
  )

const droppedRowReads = (account: SecuritiesAccount): number =>
  (account.droppedReads ?? []).filter((read) => read.unit === 'rows').length

// A segment that carries at least one securities account, paired with its parent statement so the
// rows can link back to the source file.
interface SecuritiesSegment {
  statement: Statement
  segment: StatementSegment
}

const securitiesSegments = (statements: Statement[]): SecuritiesSegment[] =>
  statements.flatMap((statement) =>
    (statement.segments ?? [])
      .filter(
        (segment) =>
          (segment.securitiesDetail?.accounts?.length ?? 0) > 0 &&
          segment.duplicateOfSegmentId == null,
      )
      .map((segment) => ({ statement, segment })),
  )

// A period plus the segment date it sorts on. The sort date is stripped before returning so the
// public shape stays PortfolioPeriod.
type DatedPeriod = PortfolioPeriod & { sortDate: string }

// One account's read on one statement, before reads are grouped into accounts.
interface AccountRead {
  last4: string
  // The brokerage brand stamped once per statement. Null on older envelopes.
  institution: string | null
  securitiesAccount: SecuritiesAccount
  period: DatedPeriod
}

const toPeriod = (
  statement: Statement,
  segment: StatementSegment,
  securitiesAccount: SecuritiesAccount,
  last4: string,
): DatedPeriod => {
  const accountsInSegment = segment.securitiesDetail?.accounts?.length ?? 0
  return {
    period: periodKey(segment),
    label: periodLabel(segment),
    // The segment date the period sorts on (end preferred, then start). A blank string sorts first.
    sortDate: segment.endDate ?? segment.startDate ?? '',
    holdings: securitiesAccount.holdings.map((holding, index) =>
      toHolding(holding, segment, statement, index, last4),
    ),
    activity: securitiesAccount.activity.map((activity, index) =>
      toActivity(activity, segment, statement, index, last4),
    ),
    realizedGains: securitiesAccount.realizedGains.map((gain, index) =>
      toRealizedGain(gain, segment, statement, index, last4),
    ),
    unvestedAwards: (securitiesAccount.unvestedAwards ?? []).map(
      (award, index) =>
        toUnvestedAward(award, segment, statement, index, last4),
    ),
    yearToDate: (securitiesAccount.yearToDate ?? []).map((figure, index) =>
      toYearToDate(figure, segment, statement, index, last4),
    ),
    cashBalance: cashBalanceFor(segment, last4, accountsInSegment),
    cashGap: cashGapFor(segment, last4),
    startDate: segment.startDate ? toCalendarDate(segment.startDate) : null,
    endDate: segment.endDate ? toCalendarDate(segment.endDate) : null,
    statementId: statement.id,
    segmentId: segment.id,
    statementFileName: statement.fileName,
    statementFileUrl: statement.fileUrl,
    validated: securitiesAccount.validated,
    failingSections: failingSections(securitiesAccount),
    printedTotals: printedTotals(securitiesAccount),
    droppedRowReads: droppedRowReads(securitiesAccount),
    holdingsOpeningMissing: securitiesAccount.holdingsOpeningMissing === true,
    printedPages: segment.printedPages ?? null,
  }
}

const accountReads = (statements: Statement[]): AccountRead[] =>
  securitiesSegments(statements).flatMap(({ statement, segment }) =>
    (segment.securitiesDetail?.accounts ?? []).map((securitiesAccount) => {
      // Typed as a string, but an account that printed no number can arrive null.
      const last4 = securitiesAccount.accountLast4 ?? ''
      return {
        last4,
        institution: segment.securitiesDetail?.institution ?? null,
        securitiesAccount,
        period: toPeriod(statement, segment, securitiesAccount, last4),
      }
    }),
  )

const printedMonthsOfRead = (read: AccountRead) =>
  read.period.endDate
    ? printedMonthsOf(read.period.startDate, read.period.endDate)
    : []

// Names are compared only by exact equality. A read with no name is no evidence either way.
const hasTwoInstitutionsInOneMonth = (reads: AccountRead[]): boolean => {
  const namesByMonth = new Map<string, Set<string>>()
  for (const read of reads) {
    if (read.institution == null) continue
    for (const month of printedMonthsOfRead(read)) {
      const names = namesByMonth.get(month) ?? new Set<string>()
      names.add(read.institution)
      namesByMonth.set(month, names)
      if (names.size > 1) return true
    }
  }
  return false
}

const last4sWithTwoInstitutions = (reads: AccountRead[]): Set<string> => {
  const readsByLast4 = new Map<string, AccountRead[]>()
  for (const read of reads) {
    if (!read.last4) continue
    readsByLast4.set(read.last4, [
      ...(readsByLast4.get(read.last4) ?? []),
      read,
    ])
  }
  return new Set(
    [...readsByLast4]
      .filter(([, sameLast4]) => hasTwoInstitutionsInOneMonth(sameLast4))
      .map(([last4]) => last4),
  )
}

// lukita sends no account id on a securities read, so the account is its last 4 in the project.
// Only two institutions reporting that last 4 for one month split it by name.
const accountKeyOf = (read: AccountRead, splitLast4s: Set<string>): string => {
  if (!read.last4) return `${read.institution ?? 'Brokerage'}-unknown`
  if (!splitLast4s.has(read.last4)) return read.last4
  return `${read.last4}|${read.institution ?? ''}`
}

const byPeriodDate = (first: AccountRead, second: AccountRead) =>
  first.period.sortDate.localeCompare(second.period.sortDate)

// The most recent statement's name, so "Webull" then "Webull Financial LLC" reads the newer one.
const latestInstitution = (reads: AccountRead[]): string =>
  [...reads]
    .sort(byPeriodDate)
    .reverse()
    .find((read) => read.institution != null)?.institution ?? 'Brokerage'

const toAccount = (id: string, reads: AccountRead[]): PortfolioAccount => {
  const securitiesAccounts = reads.map((read) => read.securitiesAccount)
  return {
    id,
    accountNumber: reads[0].last4,
    institution: latestInstitution(reads),
    accountType: toAccountType(securitiesAccounts[0].accountType),
    // Validated only while every one of its statements validated.
    validated: securitiesAccounts.every((account) => account.validated),
    failingSections: securitiesAccounts.flatMap(failingSections),
    realizedPrinted: securitiesAccounts.some(realizedPrinted),
    // Periods arrive in fetch order. Oldest first, so the chart reads left to right.
    periods: [...reads]
      .sort(byPeriodDate)
      .map(({ period: { sortDate: _sortDate, ...period } }) => period),
  }
}

// Fold statements -> segments -> securities accounts into the PortfolioAccount[] the tables consume.
// Each segment is one period of each account it read. Read-only.
export const foldSecuritiesAccounts = (
  statements: Statement[],
): PortfolioAccount[] => {
  const reads = accountReads(statements)
  const splitLast4s = last4sWithTwoInstitutions(reads)
  const readsByAccount = new Map<string, AccountRead[]>()
  for (const read of reads) {
    const key = accountKeyOf(read, splitLast4s)
    readsByAccount.set(key, [...(readsByAccount.get(key) ?? []), read])
  }
  return [...readsByAccount].map(([id, accountReadsOf]) =>
    toAccount(id, accountReadsOf),
  )
}

export const useProjectSecurities = (): PortfolioAccount[] =>
  foldSecuritiesAccounts(useStatements())
